/* eslint-disable no-useless-escape */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { Fragment, Slice, Node as ProseMirrorNode } from "prosemirror-model";
import { Mapping } from "prosemirror-transform";
import { Node as PMNode } from "prosemirror-model";
import { TextSelection } from "prosemirror-state"; // 确保导入 TextSelection
import User from "@/stores/userStore";

interface ComposeState {
  text: string;
  from: number;
  to: number;
}

interface ICursorUserInfo {
  name: string;
  position: number;
  colorInfo: string;
  id: string;
  range: {
    from: number;
    to: number;
  };
}

interface ICursorPositionProps {
  RowId: number;
  ColId: number;
  userInfo: ICursorUserInfo[];
}

export interface CharacterCountOptions {
  limit: number | null | undefined;
  mode: "textSize" | "nodeSize";
  textLength: number;
  cursorPositionInfo: ICursorPositionProps | null;
  isFormat: boolean;
  singleLine: number;
  maxLine: number
}

export interface CharacterCountStorage {
  characters: (options?: {
    node?: ProseMirrorNode;
    mode?: "textSize" | "nodeSize";
  }) => number;
  words: (options?: { node?: ProseMirrorNode }) => number;
  getColorInfo: () => string;
  setCursorPositions: (positions: ICursorUserInfo[]) => void;
}

const punctuation = [
  ",",
  "，",
  ".",
  "。",
  ";",
  "；",
  ":",
  "：",
  "?",
  "？",
  "!",
  "！",
  "…",
  "……",
  "-",
  "—",
  "、",
  "'",
  "。",
  "、",
  "．",
  "「",
  "」",
  "『",
  "』",
  "（",
  "）",
  "【",
  "】",
  " ",
];
const punctuationRegex = punctuation.map((p) => `\\${p}`).join("");
// const regex = new RegExp(`[^\\u4e00-\\u9fa5a-zA-Z0-9${punctuationRegex}]`);
const regex = new RegExp(
  `[^\\u4e00-\\u9fa5a-zA-Z0-9${punctuationRegex}ぁ-んァ-ン一-龯々〆〤\\uAC00-\\uD7AF\\u1100-\\u11FF\\u3040-\\u309F\\u30A0-\\u30FF\\u0E00-\\u0E7F]`
);
const PUNCTUATION_REGEX = /[,，.。;；:：?？!\\r\\n！………—、-]/g;
export const CharacterCount = Extension.create<
  CharacterCountOptions,
  CharacterCountStorage
>({
  name: "characterCount",

  addOptions() {
    return {
      limit: null,
      mode: "textSize",
      textLength: 0,
      cursorPositionInfo: null,
      isFormat: false,
      singleLine: 0,
      maxLine: 0
    };
  },

  addStorage() {
    return {
      characters: () => 0,
      words: () => 0,
      getColorInfo() {
        const hex = Math.floor(Math.random() * 0xffffff).toString(16);
        return "#" + ("000000" + hex).slice(-6);
      },
      setCursorPositions: () => {
        // 初始化为空函数，实际实现将在 onCreate 中定义

      },
      getLimit: () => this.options.limit,
      getSingleLine: () => this.options.singleLine
    };
  },

  onCreate() {
    const singleLine = this.options.singleLine;
    const maxLine = this.options.maxLine;
    const limit = this.options.limit;
    const isFormat = this.options.isFormat;
    let composing: any = false;
    let composeState: ComposeState;
    const pluginKey = new PluginKey("characterCount");

    // 定义 setCursorPositions 方法
    this.storage.setCursorPositions = (positions: ICursorUserInfo[]) => {
      const { editor } = this;
      if (!editor) {
        console.warn("Editor instance is not available.");
        return;
      }
      const state = editor.state;
      const transaction = state.tr.setMeta(pluginKey, {
        cursorPositions: positions,
      });
      editor.view.dispatch(transaction);
    };

    // 创建装饰插件
    const decorationPlugin = new Plugin({
      key: pluginKey,
      state: {
        init: (config, state) => ({
          cursorPositions: this.options.cursorPositionInfo?.userInfo || [],
        }),
        apply: (tr, prev) => {
          const meta = tr.getMeta(pluginKey);
          if (meta && meta.cursorPositions) {
            return {
              cursorPositions: meta.cursorPositions,
            };
          }
          return prev;
        },
      },
      props: {
        decorations: (state) => {
          const pluginState = pluginKey.getState(state);
          if (!pluginState.cursorPositions.length) return
          const decorations: Decoration[] = [];
          const extractGradientColors = (gradientStr) => {
            // 使用正则表达式匹配 hex 颜色或 rgb/rgba 颜色
            const colorRegex = /#([0-9A-Fa-f]{3,6})\b|rgba?\([^\)]+\)/g;
            const colors = [];
            let match;
            // 循环匹配所有颜色
            while ((match = colorRegex.exec(gradientStr)) !== null) {
              // match[0] 是完整匹配的颜色字符串
              colors.push(match[0]);
            }

            return colors;
          };

          pluginState.cursorPositions.forEach((position) => {
            decorations.push(
              Decoration.widget(position.position, () => {
                const cursor = document.createElement("span");
                cursor.classList.add("collaboration-cursor__caret");
                cursor.setAttribute(
                  "style",
                  `border-color:${extractGradientColors(
                    position.colorInfo,
                  ).pop()};display:${position.id != User?.userInfo?.uuid ? "inline" : "none"
                  };`,
                );
                const label = document.createElement("div");
                label.classList.add("collaboration-cursor__label");
                label.setAttribute(
                  "style",
                  `background:${position.colorInfo}; color: white;display: ${position.id != User?.userInfo?.uuid ? "block" : "none"
                  };`,
                );

                label.textContent = position.name;
                cursor.appendChild(label);
                return cursor;
              }),

              Decoration.inline(position.range.from, position.range.to, {
                style: `background-color: ${position.id != User?.userInfo?.uuid
                  ? extractGradientColors(position.colorInfo).shift()
                  : "transparent"
                  };color: ${position.id != User?.userInfo?.uuid ? "white" : "inherit"
                  }`,
              }),
            );
          });

          return DecorationSet.create(state.doc, decorations);
        },
        handleDOMEvents: {
          paste(view, event) {
            event.preventDefault();
            const { state, dispatch } = view;
            const { from, to, $from } = state.selection;
            const currentNode = $from.node($from.depth - 1);

            // 统计当前文档中的 listItem 行数（用于 maxLine）
            let currentLineCount = 0;
            state.doc.descendants((node) => {
              if (node.type.name === 'listItem') currentLineCount++;
            });

            const isInBulletList = currentNode?.type.name === "listItem";

            // —— 原始文本获取 ——（避免 JSON.stringify 引入的转义问题）
            let text = event.clipboardData?.getData("text") || "";

            // —— isFormat 过滤（保留既有逻辑）——
            if (isFormat) {
              text = text.replace(
                /[^\p{P}\p{Sc}\p{Nd}A-Za-z\u2190-\u21FF\s\u4e00-\u9fa5\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF\u0E00-\u0E7F]/gu,
                ""
              );
              if (!text) return true;
            }

            // —— 拆分行（按上下文选择分隔规则）——
            // 列表 + isFormat: 使用标点切分；否则按换行切分
            const splitToParts = () => {
              if (isInBulletList && isFormat) {
                return text
                  .split(PUNCTUATION_REGEX)
                  .map(s => s.trim())
                  .filter(Boolean);
              }
              return text
                .split(/\r\n|\n|\r/g)
                .map(s => s.trim())
                .filter(Boolean);
            };

            // —— 逐行应用 singleLine 限制（关键修复①：多行每行截断）——
            const applyPerLineLimit = (parts: string[]) => {
              if (singleLine > 0) {
                return parts.map(line => line.length > singleLine ? line.slice(0, singleLine) : line);
              }
              return parts;
            };

            // —— 计算总字数限制余量 —— 
            const currentTextLength = state.doc.textContent.length;
            const selectionLength = to - from;
            let remainingChars = limit ? Math.max(0, limit - (currentTextLength - selectionLength)) : Infinity;

            let slice: Slice | null = null;

            // —— 多行/多项构建 —— 
            const buildNodesFromParts = (parts: string[]) => {
              const { schema } = state;
              const paragraph = schema.nodes.paragraph;
              const listItem = schema.nodes.listItem;

              const items: PMNode[] = [];
              for (const raw of parts) {
                if (remainingChars <= 0) break;

                // 单行限制（逐行）已在 applyPerLineLimit 中处理，这里做总字数限制裁剪
                const partToAdd = raw.slice(0, remainingChars);
                if (!partToAdd.length) continue;

                if (isInBulletList) {
                  items.push(listItem.create({}, paragraph.create({}, schema.text(partToAdd))));
                } else {
                  items.push(paragraph.create({}, schema.text(partToAdd)));
                }
                remainingChars -= partToAdd.length;

                // 行数上限（仅针对 listItem 统计），非列表场景沿用 maxLine 作为段落上限
                if (maxLine > 0 && isInBulletList && (currentLineCount + items.length) >= maxLine) {
                  break;
                }
              }

              // 应用 maxLine（列表用 listItem 行数，普通段落按条目数）
              const truncatedItems = (() => {
                if (maxLine > 0) {
                  if (isInBulletList) {
                    return items.slice(0, Math.max(0, maxLine - currentLineCount));
                  }
                  return items.slice(0, maxLine); // 非列表场景按条目限制
                }
                return items;
              })();

              if (truncatedItems.length > 0) {
                return new Slice(Fragment.fromArray(truncatedItems), 0, 0);
              }
              return null;
            };

            // —— 尝试按“多行/多项”构建 —— 
            let parts = splitToParts();
            parts = applyPerLineLimit(parts);

            if (parts.length > 1) {
              // 多行/多项：直接构建 Slice（关键修复②：多行也受 singleLine/limit/maxLine）
              slice = buildNodesFromParts(parts);
            } else {
              // 单行：保留到后面继续做“当前行剩余 + 选择范围”下的单行限制与总字数限制
              // 此处不立即构建 slice，让下面的单行逻辑处理
            }

            // —— 单行粘贴（或上面没构建出 slice 的情况）——
            let original = parts.length === 1 ? parts[0] : text;

            if (!slice) {
              // 关键修复③：即便在“全选整行/整段”时，也要应用单行限制
              if (singleLine > 0) {
                const lineStart = $from.start();
                const lineEnd = $from.end();
                const lineText = state.doc.textBetween(lineStart, lineEnd, "", "");
                const before = lineText.slice(0, from - lineStart);
                const after = lineText.slice(to - lineStart);

                // 选择范围覆盖整行时，allowed = singleLine；否则按行内剩余计算
                let allowed = singleLine - (before.length + after.length);
                if (from === lineStart && to === lineEnd) {
                  allowed = singleLine;
                }
                if (allowed <= 0) return true;

                // 按单行限制截断
                if (original.length > allowed) {
                  original = original.slice(0, allowed);
                }
              }

              // —— 总字数限制（limit）——
              if (limit) {
                const allowedByLimit = Math.max(0, limit - (currentTextLength - selectionLength));
                if (allowedByLimit <= 0) return true;
                if (original.length > allowedByLimit) {
                  original = original.slice(0, allowedByLimit);
                }
              }

              if (!original.length) return true;

              // 构建单文本 Slice
              const { schema } = state;
              slice = new Slice(Fragment.from(schema.text(original)), 0, 0);
            }

            // —— 最终替换 —— 
            if (slice) {
              const tr = state.tr.replaceSelection(slice);
              tr.scrollIntoView();
              dispatch(tr);
            }
            return true;
          },
          compositionstart(view) {
            composing = true;
            return true;
          },
          compositionend(view) {
            composing = false;
            const currentDoc = view.state.doc;

            // 如果没有设置 limit，直接返回
            if (!limit) {
              return false;
            }

            // 如果文档内容长度小于限制，直接返回
            if (currentDoc.textContent.length < limit) {
              return false;
            }

            // 创建事务，删除超出部分的内容
            const over = currentDoc.textContent.length - limit;
            const tr = view.state.tr.delete(
              composeState.to - over,
              composeState.to,
            );
            // 应用事务并更新视图
            view.dispatch(tr);
            return true;
          },
        },

        handleTextInput: (view, from, to, text) => {
          // 如果单行限制存在，移除文本中的换行符
          const cleanText =
            singleLine > 0 ? text.replace(/[\r\n]+/g, " ") : text;

          if (regex.test(cleanText)) {
            // 阻止不符合的字符被输入
            return true;
          }
          if (composing) {
            composeState = {
              text: cleanText,
              from: from,
              to: from + cleanText.length,
            };
            return false;
          }
          // 获取当前文档的内容
          const currentDoc = view.state.doc;
          // 计算新插入的文本长度
          const newContentLength =
            currentDoc.textContent.length + cleanText.length - (to - from);

          // 如果单行限制存在，确保当前行不超过限制
          if (singleLine > 0) {
            const $from = view.state.doc.resolve(from);
            const currentLineStart = $from.start();
            const currentLineEnd = $from.end();

            const currentLineText = view.state.doc.textBetween(
              currentLineStart,
              currentLineEnd,
              "",
            );
            const newLineText =
              currentLineText.slice(0, from - currentLineStart) +
              cleanText +
              currentLineText.slice(to - currentLineStart);

            if (newLineText.length > singleLine) {
              // 如果新行文本长度超过限制，截断剩余字符
              const allowedChars =
                singleLine -
                (from - currentLineStart) -
                (currentLineText.length - to + from);
              const truncatedText = cleanText.slice(0, allowedChars);
              // 插入截断后的文本
              view.dispatch(view.state.tr.insertText(truncatedText, from, to));
              return true;
            }
          }

          // 字数限制
          if (limit && newContentLength > limit) {
            return true;
          }

          // 如果没有超过限制，允许插入
          return false;
        },
      },
    });

    // 将插件添加到编辑器
    if (this.editor) {
      this.editor.view.updateState(
        this.editor.view.state.reconfigure({
          plugins: this.editor.view.state.plugins.concat([decorationPlugin]),
        }),
      );
    } else {
      console.warn(
        "Editor instance is not available when adding decorationPlugin.",
      );
    }
  },

  onBeforeCreate() {
    // 初始化字符和词数统计方法
    this.storage.characters = (options) => {
      const node = options?.node || this.editor?.state.doc;
      if (!node) return 0;

      const mode = options?.mode || this.options.mode;

      if (mode === "textSize") {
        const text = node.textBetween(0, node.content.size, "");
        return text.length;
      }

      return node.nodeSize;
    };

    this.storage.words = (options) => {
      const node = options?.node || this.editor?.state.doc;
      if (!node) return 0;

      const text = node.textBetween(0, node.content.size, " ", " ");
      const words = text.split(" ").filter((word) => word !== "");
      return words.length;
    };
  },

  addProseMirrorPlugins() {
    // 此方法可以留空，因为我们已经在 onCreate 中添加了插件
    return [];
  },
});
